import React from "react";

type TimelineViewWeeklyProps = {
    shippingData: any[];
    currentYear: number;
};
const TimelineViewWeekly = (props: TimelineViewWeeklyProps) => {
    // Helper function to get week number for a date
    function getWeekNumber(date: Date): number {
        const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
        const pastDaysOfYear =
            (date.getTime() - firstDayOfYear.getTime()) / 86400000;
        return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    }

    // Helper function to check if a date falls within a specific week
    function isDateInWeek(dateString: string, weekLabel: string): boolean {
        if (!dateString) return false;

        // Parse the week label (e.g., "W1 Jan 1-7")
        const weekParts = weekLabel.split(" ");
        if (weekParts.length < 3) {
            console.error("Invalid week format. Expected format: 'W1 Jan 1-7'");
            return false;
        }

        // Extract week number (remove "W" prefix)
        const weekNumber = parseInt(weekParts[0].substring(1));

        // Parse the date string
        const [dateYear, dateMonthStr, dateDay] = dateString
            .split("-")
            .map(Number);
        const inputDate = new Date(dateYear, dateMonthStr - 1, dateDay);

        // Get week number for the input date
        const inputWeekNumber = getWeekNumber(inputDate);

        // Check if the date's week number and year match
        if (inputWeekNumber > 52) {
            if (weekNumber == 52 && inputWeekNumber == 53) {
                return true;
            }
            return false;
        } else {
            return (
                inputWeekNumber === weekNumber &&
                inputDate.getFullYear() === dateYear
            );
        }
    }

    // Helper function to get current week
    function getCurrentWeek(): number {
        const currentDate = new Date();
        return getWeekNumber(currentDate);
    }

    // Helper function to check if a week is the current week
    function isCurrentWeek(weekLabel: string): boolean {
        const currentWeek = getCurrentWeek();
        const currentYear = new Date().getFullYear();

        const weekParts = weekLabel.split(" ");
        const weekNumber = parseInt(weekParts[0].substring(1));

        return weekNumber === currentWeek && props.currentYear === currentYear;
    }

    // Helper function to check if an item is late for a specific week
    function isItemLateForWeek(row: any, weekLabel: string): boolean {
        if (!row.date_planned || row.effective_date) return false;

        const weekParts = weekLabel.split(" ");
        const weekNumber = parseInt(weekParts[0].substring(1));

        const plannedDate = new Date(row.date_planned);
        const currentDate = new Date();

        // Get week number for planned date
        const plannedWeek = getWeekNumber(plannedDate);
        const plannedYear = plannedDate.getFullYear();

        // Get current week number
        const currentWeek = getWeekNumber(currentDate);
        const currentYear = currentDate.getFullYear();

        // Create date for the end of the target week
        const targetWeekEnd = new Date(props.currentYear, 0, 1);
        targetWeekEnd.setDate(targetWeekEnd.getDate() + weekNumber * 7 - 1);

        // If planned date is before or during this week AND we're past this week AND item not shipped/delivered
        if (
            (plannedYear < props.currentYear ||
                (plannedYear === props.currentYear &&
                    plannedWeek <= weekNumber)) &&
            (currentYear > props.currentYear ||
                (currentYear === props.currentYear &&
                    currentWeek > weekNumber)) &&
            !row.shipped_date &&
            !row.effective_date
        ) {
            return true;
        }

        return false;
    }

    // Helper function to generate week labels for the year
    function generateWeekLabels(year: number): string[] {
        const weekLabels: string[] = [];
        const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ];

        // Start from January 1st of the given year
        let currentDate = new Date(year, 0, 1);

        // Find the first Monday (ISO week starts on Monday)
        while (currentDate.getDay() !== 1) {
            currentDate.setDate(currentDate.getDate() + 1);
        }

        for (let weekNum = 1; weekNum <= 52; weekNum++) {
            const weekStart = new Date(currentDate);
            const weekEnd = new Date(currentDate);
            weekEnd.setDate(weekEnd.getDate() + 6);

            // Format: "W1 Jan 1-7"
            const startMonth = monthNames[weekStart.getMonth()];
            const startDay = weekStart.getDate();

            if (weekStart.getMonth() === weekEnd.getMonth()) {
                // Same month
                const endDay = weekEnd.getDate();
                weekLabels.push(
                    `W${weekNum} ${startMonth} ${startDay}-${endDay}`
                );
            } else {
                // Cross-month week
                const endMonth = monthNames[weekEnd.getMonth()];
                const endDay = weekEnd.getDate();
                weekLabels.push(
                    `W${weekNum} ${startMonth} ${startDay}-${endMonth} ${endDay}`
                );
            }

            // Move to next week
            currentDate.setDate(currentDate.getDate() + 7);
        }

        return weekLabels;
    }

    // Main chip rendering function for weekly view
    const renderChips = (row: any, weekLabel: string) => {
        const chips = [];

        // Check for ETA (date_planned in this week)
        const isETASameWeek = isDateInWeek(row.date_planned, weekLabel);

        // Check for Ship Arrived (shipped_date in this week)
        const isShipArrivedSameWeek =
            row.shipped_date && isDateInWeek(row.shipped_date, weekLabel);

        // Check for Delivery (effective_date in this week)
        const isDeliverySameWeek =
            row.effective_date &&
            !row.is_dropship &&
            isDateInWeek(row.effective_date, weekLabel);

        // Check for Inspection (dropship_inspection_date in this week)
        const isInspectionSameWeek =
            row.dropship_inspection_date &&
            isDateInWeek(row.dropship_inspection_date, weekLabel);

        // Check for Late status
        const isLate = isItemLateForWeek(row, weekLabel);

        // Priority logic: ETA and Late should not appear together in same cell
        if (isETASameWeek && isLate) {
            // If both ETA and Late would appear, show only Late (higher priority for overdue items)
            chips.push(
                <span
                    key="eta"
                    className="badge"
                    style={{
                        backgroundColor: "#22cc00ff",
                        fontWeight: "700",
                        fontSize: "0.7rem",
                        color: "#333",
                        margin: "1px",
                    }}
                >
                    ETA
                </span>
            );
        } else {
            // Show ETA if not conflicting with Late
            if (isETASameWeek) {
                chips.push(
                    <span
                        key="eta"
                        className="badge"
                        style={{
                            backgroundColor: "#22cc00ff",
                            fontWeight: "700",
                            fontSize: "0.7rem",
                            color: "#333",
                            margin: "1px",
                        }}
                    >
                        ETA
                    </span>
                );
            }

            // Show Late if not conflicting with ETA
            if (isLate) {
                chips.push(
                    <span
                        key="late"
                        className="badge"
                        style={{
                            backgroundColor: "#f57242ff",
                            fontWeight: "700",
                            fontSize: "0.7rem",
                            color: "#333",
                            display: "inline",
                            margin: "1px",
                        }}
                    >
                        Late
                    </span>
                );
            }
        }

        // Add other chips (these don't conflict with ETA/Late)
        if (isShipArrivedSameWeek) {
            chips.push(
                <span
                    key="ship-arrived"
                    className="badge"
                    style={{
                        backgroundColor: "#22cc00ff",
                        fontWeight: "700",
                        fontSize: "0.7rem",
                        color: "#333",
                        display: "inline",
                        margin: "1px",
                    }}
                >
                    Ship Arrived
                </span>
            );
        }

        if (isDeliverySameWeek) {
            chips.push(
                <span
                    key="delivery"
                    className="badge"
                    style={{
                        backgroundColor: "#22cc00ff",
                        fontWeight: "700",
                        fontSize: "0.7rem",
                        color: "#333",
                        display: "inline",
                        margin: "1px",
                    }}
                >
                    Receipt
                </span>
            );
        }

        if (isInspectionSameWeek) {
            chips.push(
                <span
                    key="inspection"
                    className="badge"
                    style={{
                        backgroundColor: "#22cc00ff",
                        fontWeight: "700",
                        fontSize: "0.7rem",
                        color: "#333",
                        display: "inline",
                        margin: "1px",
                    }}
                >
                    Inspected
                </span>
            );
        }

        return chips;
    };

    // Generate all 52 weeks for the current year
    const generateWeekColumns = (): string[] => {
        return generateWeekLabels(props.currentYear);
    };

    const weekColumns = generateWeekColumns();

    return (
        <table
            className="table table-hover mb-0"
            style={{
                borderRight: "1px solid #efefef",
            }}
        >
            <thead
                style={{
                    position: "sticky",
                    top: 0,
                    color: "white",
                    zIndex: 10,
                }}
            >
                <tr>
                    {weekColumns.map((weekLabel: string, idx) => (
                        <th
                            key={idx}
                            className="fw-semibold text-center"
                            style={{
                                fontSize: "0.7rem",
                                color: "#6c757d",
                                padding: "0.6rem",
                                borderBottom: "2px solid #dee2e6",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {weekLabel.split(" ")[0]}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {props.shippingData.map((row: any, idx) => (
                    <tr
                        key={idx}
                        style={{
                            borderBottom: "1px solid #f0f0f0",
                            height: 50,
                        }}
                    >
                        {weekColumns.map((weekLabel, weekIdx) => (
                            <td
                                key={weekIdx}
                                className="text-center"
                                style={{
                                    padding: "0.5rem",
                                    minWidth: "100px",
                                    backgroundColor: isCurrentWeek(weekLabel)
                                        ? "#6bb6deff"
                                        : "#fff",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        overflow: "visible",
                                    }}
                                >
                                    {renderChips(row, weekLabel)}
                                </div>
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
            <style>{`
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  
  .scrollbar-hide {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
  
  .table-hover tbody tr:hover {
    background-color: #f8f9fa;
  }
`}</style>
        </table>
    );
};

export default TimelineViewWeekly;
